import { useState } from 'react'
import type { AdminParameter, ParameterKind, ParameterWrite } from '../api/types'

const KINDS: { value: ParameterKind; label: string; note: string }[] = [
  { value: 'instant', label: 'Instantaneous',
    note: 'a reading at a moment: stage, discharge, temperature. Charted as '
        + 'a line, broken across outages.' },
  { value: 'counter_lifetime', label: 'Lifetime counter',
    note: 'a running total that never resets. Charted as steps; volume is '
        + 'the sum of its positive increments, never an average.' },
  { value: 'counter_daily', label: 'Daily counter',
    note: 'a running total that resets once a day: precipitation, daily '
        + 'volume. Charted as steps; QC flags the reset.' },
  { value: 'direction', label: 'Direction',
    note: 'a bearing in degrees, so 359 and 1 are two apart. QC flags a '
        + 'vane reading 0 at zero wind speed as the calm sentinel.' },
  { value: 'status', label: 'Status',
    note: 'a discrete state. Charted as steps, never as a line between '
        + 'states.' },
]

interface Props {
  /** Absent when defining a new one. */
  parameter?: AdminParameter
  busy: boolean
  onSubmit: (body: ParameterWrite) => void
  onCancel: () => void
}

/** Define or edit a quantity the network can measure.
 *
 *  `kind` is the field that matters: it decides how every reading stored
 *  under this parameter is charted and which QC rules flag the next one,
 *  for the whole archive at once. The form spells out what each one means
 *  rather than offering five words from an enum.
 */
export function ParameterForm({ parameter, busy, onSubmit, onCancel }: Props) {
  const editing = parameter !== undefined
  const [form, setForm] = useState<Record<string, string>>(() => ({
    code: parameter?.code ?? '',
    display_name: parameter?.display_name ?? '',
    unit: parameter?.unit ?? '',
    axis_group: parameter?.axis_group ?? '',
    min_valid: parameter?.min_valid == null ? '' : String(parameter.min_valid),
    max_valid: parameter?.max_valid == null ? '' : String(parameter.max_valid),
    max_rate: parameter?.max_rate == null ? '' : String(parameter.max_rate),
    description: parameter?.description ?? '',
  }))
  const [kind, setKind] = useState<ParameterKind>(parameter?.kind ?? 'instant')

  const set = (key: string) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }))

  function submit() {
    const body: ParameterWrite = {}
    const numeric = ['min_valid', 'max_valid', 'max_rate']

    for (const [key, value] of Object.entries(form)) {
      if (editing && key === 'code') continue
      const trimmed = value.trim()

      if (trimmed === '') {
        // Only clears something that had a value — sending null for a field
        // that was already null fills the audit log with phantom changes.
        const was = (parameter as unknown as Record<string, unknown>)?.[key]
        if (editing && was !== null && was !== undefined && was !== '') {
          ;(body as Record<string, unknown>)[key] = null
        }
        continue
      }
      const parsed = numeric.includes(key) ? Number(trimmed) : trimmed
      if (editing) {
        const before = (parameter as unknown as Record<string, unknown>)[key]
        if (String(before ?? '') === String(parsed)) continue
      }
      ;(body as Record<string, unknown>)[key] = parsed
    }
    if (!editing || kind !== parameter.kind) body.kind = kind
    onSubmit(body)
  }

  const chosen = KINDS.find((k) => k.value === kind)

  return (
    <div className="form">
      <div className="form-grid">
        <label className="field">
          <span>Code</span>
          <input value={form.code} onChange={set('code')} disabled={editing}
                 placeholder="snow_depth" />
          <small className="muted">
            {editing
              ? 'Identity: it is the column header in every export.'
              : 'Lowercase, underscores. Becomes an export column header.'}
          </small>
        </label>

        <label className="field">
          <span>Display name</span>
          <input value={form.display_name} onChange={set('display_name')}
                 placeholder="Snow Depth" />
        </label>

        <label className="field">
          <span>Unit</span>
          <input value={form.unit} onChange={set('unit')} placeholder="cm" />
          <small className="muted">
            Use <code>-</code> for a dimensionless quantity.
          </small>
        </label>

        <label className="field">
          <span>Axis group</span>
          <input value={form.axis_group} onChange={set('axis_group')}
                 placeholder="snow" />
          <small className="muted">
            Everything in a group shares one Y axis, so it must share a unit.
            Not the same as the unit: water level and water depth are both
            metres and belong apart.
          </small>
        </label>

        <label className="field">
          <span>Valid range, low</span>
          <input value={form.min_valid} onChange={set('min_valid')}
                 inputMode="decimal" placeholder="optional" />
        </label>

        <label className="field">
          <span>Valid range, high</span>
          <input value={form.max_valid} onChange={set('max_valid')}
                 inputMode="decimal" placeholder="optional" />
          <small className="muted">
            Leave empty until real readings show the spread: thresholds come
            from observed data, not from units. A value outside the range is
            flagged, never discarded.
          </small>
        </label>

        <label className="field">
          <span>Max change per second</span>
          <input value={form.max_rate} onChange={set('max_rate')}
                 inputMode="decimal" placeholder="optional" />
          <small className="muted">Faster than this is flagged as a spike.</small>
        </label>

        <label className="field">
          <span>Description</span>
          <input value={form.description} onChange={set('description')}
                 placeholder="optional" />
        </label>
      </div>

      <label className="field">
        <span>What it is</span>
        <select value={kind}
                onChange={(e) => setKind(e.target.value as ParameterKind)}>
          {KINDS.map((k) => (
            <option key={k.value} value={k.value}>{k.label}</option>
          ))}
        </select>
        <small className="muted">{chosen?.note}</small>
        {editing && kind !== parameter.kind && (
          <small className="muted">
            This changes how every reading already stored is read back.
          </small>
        )}
      </label>

      <div className="form-actions">
        <button className="button primary" onClick={submit} disabled={busy}>
          {busy ? 'Saving…' : editing ? 'Save changes' : 'Define parameter'}
        </button>
        <button className="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  )
}
